import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import MainContentContainer from "../../MainContentContainer/MainContentContainer";
import {
  createFeedbackExport,
  checkFeedbackExportStatus,
  fetchFeedbackExports,
  downloadFeedbackExport,
} from "../../../actions/researchFeedbackActions";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import { format, isValid as isValidDate } from "date-fns";

// session_type matches the master research export's derived value:
// 'session' = standalone, 'series' = part of a series, 'challenge' = part of a challenge.
// Empty string = "All" — sent as no filter.
const SESSION_TYPES = [
  { value: "", label: "All" },
  { value: "session", label: "Session (standalone)" },
  { value: "series", label: "Series session" },
  { value: "challenge", label: "Challenge session" },
];

const ResearchUserFeedbackPage = () => {
  const dispatch = useDispatch();

  // Filter state
  const [contentSessionId, setContentSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionType, setSessionType] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [includeQuickTaps, setIncludeQuickTaps] = useState(false);

  // Triggering / polling
  const [isExporting, setIsExporting] = useState(false);

  // Past exports
  const [exports, setExports] = useState([]);
  const [isLoadingExports, setIsLoadingExports] = useState(false);
  const pollingRef = useRef({});
  // Tracks consecutive polling failures per job; we stop polling after MAX_POLL_FAILURES
  // to prevent infinite Swal popups when the API is persistently down.
  const pollingFailuresRef = useRef({});
  const MAX_POLL_FAILURES = 5;

  // Download state — tracks multiple concurrent downloads
  const [downloadingIds, setDownloadingIds] = useState(new Set());

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // ──────────────────────────────────────────────────────────────────────
  // Loading / polling
  // ──────────────────────────────────────────────────────────────────────

  const stopPolling = useCallback((jobId) => {
    if (pollingRef.current[jobId]) {
      clearInterval(pollingRef.current[jobId]);
      delete pollingRef.current[jobId];
    }
    delete pollingFailuresRef.current[jobId];
  }, []);

  const startPolling = useCallback(
    (jobId) => {
      if (pollingRef.current[jobId]) return;
      pollingFailuresRef.current[jobId] = 0;

      pollingRef.current[jobId] = setInterval(async () => {
        try {
          const updated = await dispatch(checkFeedbackExportStatus(jobId));

          if (!updated) {
            // checkFeedbackExportStatus resolves null on error (errorHandler
            // already showed the toast). Bail out after a few failures so we
            // don't keep firing modal popups.
            pollingFailuresRef.current[jobId] =
              (pollingFailuresRef.current[jobId] || 0) + 1;
            if (pollingFailuresRef.current[jobId] >= MAX_POLL_FAILURES) {
              console.error(
                `Polling stopped for feedback export #${jobId} after ${MAX_POLL_FAILURES} consecutive failures`
              );
              stopPolling(jobId);
            }
            return;
          }

          // Reset failure counter on success
          pollingFailuresRef.current[jobId] = 0;

          setExports((prev) =>
            prev.map((j) => (j.job_id === jobId ? updated : j))
          );

          if (updated.status === "completed" || updated.status === "failed") {
            stopPolling(jobId);
            if (updated.status === "completed") {
              showSnackbar(
                `Export #${jobId} ready: ${updated.row_count || 0} rows`,
                "success"
              );
            } else {
              showSnackbar(
                `Export #${jobId} failed: ${updated.error_message || "Unknown error"}`,
                "error"
              );
            }
          }
        } catch (err) {
          // Defensive — checkFeedbackExportStatus shouldn't throw, but if it
          // does we stop polling so the user isn't spammed.
          console.error(`Failed to check status for feedback export #${jobId}:`, err);
          stopPolling(jobId);
        }
      }, 5000);
    },
    [dispatch, showSnackbar, stopPolling]
  );

  const loadExports = useCallback(async () => {
    setIsLoadingExports(true);
    try {
      const data = await dispatch(fetchFeedbackExports());
      const list = Array.isArray(data) ? data : data?.data || [];
      setExports(list);

      // Re-attach pollers for any rows that are still pending/processing
      list.forEach((job) => {
        if (
          (job.status === "pending" || job.status === "processing") &&
          !pollingRef.current[job.job_id]
        ) {
          startPolling(job.job_id);
        }
      });
    } finally {
      setIsLoadingExports(false);
    }
  }, [dispatch, startPolling]);

  useEffect(() => {
    loadExports();
    const intervals = pollingRef.current;
    return () => {
      Object.values(intervals).forEach(clearInterval);
    };
  }, [loadExports]);

  // ──────────────────────────────────────────────────────────────────────
  // Trigger export
  // ──────────────────────────────────────────────────────────────────────

  const handleExport = useCallback(async () => {
    const filters = {};
    if (contentSessionId)
      filters.content_session_id = parseInt(contentSessionId, 10);
    if (sessionName.trim()) filters.session_name = sessionName.trim();
    if (sessionType) filters.session_type = sessionType;
    if (dateFrom && isValidDate(dateFrom))
      filters.from_date = format(dateFrom, "yyyy-MM-dd");
    if (dateTo && isValidDate(dateTo))
      filters.to_date = format(dateTo, "yyyy-MM-dd");
    if (includeQuickTaps) filters.include_quick_taps = true;

    setIsExporting(true);
    try {
      const job = await dispatch(createFeedbackExport(filters));
      if (job?.job_id) {
        showSnackbar(`Export #${job.job_id} queued`, "info");
        setExports((prev) => [job, ...prev]);
        startPolling(job.job_id);
      }
    } catch (e) {
      // errorHandler already showed an alert
    } finally {
      setIsExporting(false);
    }
  }, [
    dispatch,
    contentSessionId,
    sessionName,
    sessionType,
    dateFrom,
    dateTo,
    includeQuickTaps,
    showSnackbar,
    startPolling,
  ]);

  // ──────────────────────────────────────────────────────────────────────
  // Download
  // ──────────────────────────────────────────────────────────────────────

  const handleDownload = useCallback(
    async (job) => {
      setDownloadingIds((prev) => new Set([...prev, job.job_id]));
      try {
        // The CSV is delivered gzipped — make sure the saved filename ends
        // in .gz, but don't double-up if the API already returns a *.gz name.
        const baseName = job.file_name || `research_feedback_export_${job.job_id}.csv`;
        const fileName = baseName.endsWith(".gz") ? baseName : `${baseName}.gz`;

        const result = await dispatch(downloadFeedbackExport(job.job_id, fileName));
        if (result?.success) {
          showSnackbar(`Downloaded ${fileName}`, "success");
        }
      } finally {
        setDownloadingIds((prev) => {
          const next = new Set(prev);
          next.delete(job.job_id);
          return next;
        });
      }
    },
    [dispatch, showSnackbar]
  );

  // ──────────────────────────────────────────────────────────────────────
  // Helpers
  // ──────────────────────────────────────────────────────────────────────

  const statusChip = (status) => {
    const colors = {
      pending: "default",
      processing: "info",
      completed: "success",
      failed: "error",
    };
    return <Chip size="small" label={status} color={colors[status] || "default"} />;
  };

  const filterSummary = (filters) => {
    if (!filters || Object.keys(filters).length === 0) return "All feedbacks";
    const parts = [];
    // Backward-compat: legacy exports stored the catalog filter under
    // `session_id` before the rename. Surface either key under the new label.
    const contentId = filters.content_session_id ?? filters.session_id;
    if (contentId) parts.push(`content_session_id=${contentId}`);
    if (filters.session_name) parts.push(`name~"${filters.session_name}"`);
    if (filters.session_type) parts.push(`type=${filters.session_type}`);
    if (filters.from_date) parts.push(`from=${filters.from_date}`);
    if (filters.to_date) parts.push(`to=${filters.to_date}`);
    if (filters.include_quick_taps) parts.push("incl. quick taps");
    return parts.length ? parts.join(", ") : "All feedbacks";
  };

  // ──────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────

  return (
    <MainContentContainer>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AppBar
          position="static"
          color="default"
          elevation={0}
          sx={{ borderBottom: "1px solid #e0e0e0" }}
        >
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Research User Feedback
            </Typography>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 3 }}>
          {/* Filters */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Filters
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 2,
                mb: 2,
              }}
            >
              <TextField
                label="Content Session ID"
                helperText="sessions.session_id (catalog)"
                type="number"
                value={contentSessionId}
                onChange={(e) => setContentSessionId(e.target.value)}
                size="small"
              />
              <TextField
                label="Session name (contains)"
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value)}
                size="small"
                placeholder='e.g. "You Are Enough"'
              />
              <FormControl size="small">
                <InputLabel>Session type</InputLabel>
                <Select
                  value={sessionType}
                  label="Session type"
                  onChange={(e) => setSessionType(e.target.value)}
                >
                  {SESSION_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <DatePicker
                label="From date"
                value={dateFrom}
                onChange={setDateFrom}
                slotProps={{ textField: { size: "small" } }}
              />
              <DatePicker
                label="To date"
                value={dateTo}
                onChange={setDateTo}
                slotProps={{ textField: { size: "small" } }}
              />
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={includeQuickTaps}
                  onChange={(e) => setIncludeQuickTaps(e.target.checked)}
                />
              }
              label="Include Quick Tap feedbacks (excluded by default)"
            />

            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleExport}
                disabled={isExporting}
                startIcon={isExporting ? <CircularProgress size={18} /> : null}
              >
                {isExporting ? "Queuing…" : "Generate export"}
              </Button>
              <Button
                variant="outlined"
                onClick={loadExports}
                disabled={isLoadingExports}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              The export contains anonymized rows only —{" "}
              <code>SHA-256(user_id)</code> matches the master research export, no
              names or emails are included.
            </Typography>
          </Paper>

          {/* Past exports */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
              <Typography variant="subtitle1">Past exports</Typography>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Filters</TableCell>
                    <TableCell align="right">Rows</TableCell>
                    <TableCell align="right">Users</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exports.length === 0 && !isLoadingExports && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography color="text.secondary" sx={{ py: 3 }}>
                          No exports yet — click "Generate export" above.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {exports.map((job) => (
                    <TableRow key={job.job_id} hover>
                      <TableCell>{job.job_id}</TableCell>
                      <TableCell>{statusChip(job.status)}</TableCell>
                      <TableCell>
                        <Tooltip title={JSON.stringify(job.filters || {}, null, 2)}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 300,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {filterSummary(job.filters)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        {job.row_count ?? "—"}
                      </TableCell>
                      <TableCell align="right">
                        {job.unique_users ?? "—"}
                      </TableCell>
                      <TableCell>{job.created_at}</TableCell>
                      <TableCell>{job.completed_at || "—"}</TableCell>
                      <TableCell align="center">
                        {job.status === "completed" ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                              downloadingIds.has(job.job_id) ? (
                                <CircularProgress size={14} />
                              ) : (
                                <DownloadIcon />
                              )
                            }
                            disabled={downloadingIds.has(job.job_id)}
                            onClick={() => handleDownload(job)}
                          >
                            Download
                          </Button>
                        ) : job.status === "failed" ? (
                          <Tooltip title={job.error_message || "Failed"}>
                            <span>—</span>
                          </Tooltip>
                        ) : (
                          <CircularProgress size={18} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </LocalizationProvider>
    </MainContentContainer>
  );
};

export default ResearchUserFeedbackPage;
